import React, { useState } from 'react';
import { Crown, X, Mail } from 'lucide-react';
import { sendTrialEndedEmail } from '../../lib/sendEmail';
import { auth } from '../../lib/firebase';
import { shouldBypassSubscription } from '../../lib/platform';

interface SubscriptionBannerProps {
  daysLeft: number;
  onClose: () => void;
  onUpgrade: () => void;
}

export function SubscriptionBanner({ daysLeft, onClose, onUpgrade }: SubscriptionBannerProps) {
  // Never show subscription banner on WebViewGold iOS and Safari iOS/iPad (App Store compliance)
  if (shouldBypassSubscription()) return null;

  const [sending, setSending] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleResendEmail = async () => {
    if (!auth.currentUser?.email) {
      setError('No email address found');
      return;
    }
    
    try {
      setError(null);
      setSending(true);
      console.log('Attempting to send trial ended email to:', auth.currentUser.email);

      // Use the sendTrialEndedEmail function from lib/sendEmail
      await sendTrialEndedEmail(auth.currentUser.email);
      console.log('Email sent successfully');
      
      // Success
      setEmailSent(true);
      setTimeout(() => setEmailSent(false), 5000);
    } catch (error) {
      console.error('Error sending email:', error);
      const errorMessage = error instanceof Error 
        ? error.message 
        : 'Failed to send email. Please try again later.';
      setError(errorMessage);
    } finally {
      setSending(false);
    }
  };
  
  return (
    <div className="bg-gradient-to-r from-blue-400/90 to-blue-600/90 rounded-b-xl text-white p-0 relative z-10 mx-[17px]">
      <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between">
        <div className="flex items-center gap-0">
          <Crown className="w-4 h-4 hidden sm:block text-white ml-2" />
          <div>
            <span className="text-sm ml-2">
              {daysLeft > 0
                ? `You are enjoying a FREE trial – ${daysLeft} day${daysLeft > 1 ? 's' : ''} remaining`
                : 'Your free trial has baked its last loaf...'}
            </span>
            {daysLeft === 0 && (
              <button
                onClick={handleResendEmail}
                disabled={sending || emailSent}
                className={`text-sm text-white/80 ml-2 hover:text-white hover:underline ${
                  sending || emailSent ? 'opacity-70 cursor-not-allowed' : 'cursor-pointer'
                }`}
              >
                Want more? Click here and check your emails!
              </button>
            )}
          </div>
          {emailSent && (
            <span className="text-white text-sm bg-green-500/20 px-3 py-1 rounded-full ml-2">
              Email sent!
            </span>
          )}
          {error && (
            <span className="text-white text-sm bg-red-500/20 px-3 py-1 rounded-full ml-2 max-w-md overflow-hidden">
              {error}
            </span>
          )}
        </div> 
      </div>
      <button
        onClick={onClose}
        className="absolute top-1 right-4 text-white/80 hover:text-white"
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  );
}